/**
 * The verbal consent an advisor records before entering a client's answers for them (docs/43 §8.5,
 * D6; crossmarc/steward-app#2538). Wording: Matt (docs/40 F1, 2026-10-24), changed by Matt on
 * 2026-11-02 to ALWAYS begin "The client": the sentence carries no client name at all, so nothing
 * a staffer can type (a staff-entered first name, say) ever reaches the stored consent.
 *
 * The same rules as {@link INTAKE_SHARING_CONSENT_TEMPLATE}'s composer (`intakeDraft.ts`), each
 * pinned in `staffEntryConsent.test.ts`:
 *   - This line, and only this line, holds the wording. The façade composes the STORED sentence
 *     through {@link staffEntryConsentSentence} from server data (the firm's name and the server's
 *     date), and the SPA renders the preview through the same function. The browser never sends
 *     the text: the façade's consent body is a strict object with no text key.
 *   - Each token appears exactly once, and every value goes in through a replacer FUNCTION, so a
 *     `$&` or `$'` inside a firm name is copied literally (crossmarc/steward-app#2081).
 *   - The sentence names no button label.
 *
 * Editing the wording means a new {@link STAFF_ENTRY_CONSENT_TEXT_VERSION}. A stored `text` is
 * written once and never re-derived, so a record keeps the sentence the advisor was shown.
 */
import { INTAKE_SHARING_CONSENT_FALLBACK_FIRM } from './intakeDraft.js';

/** Matt's sentence, verbatim (2026-11-02: always "The client", never a name). */
export const STAFF_ENTRY_CONSENT_TEMPLATE =
  'The client agreed on {date} that {firm} may enter their answers on their behalf and keep them with their client record.';

/**
 * The id of the wording above, stored as `textVersion` (docs/43 §8.5). v1 (2026-10-24, with a
 * `{Client name}` token) was never recorded on any row, so v2 replaces it with nothing to carry.
 */
export const STAFF_ENTRY_CONSENT_TEXT_VERSION = 'staff-entry-v2-2026-11-02';

export const STAFF_ENTRY_CONSENT_DATE_TOKEN = '{date}';
export const STAFF_ENTRY_CONSENT_FIRM_TOKEN = '{firm}';

/**
 * Upper bound on a composed sentence, in UTF-16 units. The template is 107 units without its
 * tokens; a 240-unit firm name (120 astral characters) and an 18-unit date make 365, so 1000
 * leaves room and still bounds what is stored (measured 2026-11-02, pinned in the test).
 */
export const STAFF_ENTRY_CONSENT_MAX_LENGTH = 1000;

/**
 * The `{date}` value: a calendar date in UTC, e.g. "November 2, 2026". UTC on both sides, so the
 * server's stored sentence and the browser's preview agree on the day for the same instant,
 * whatever the advisor's own time zone. Throws on an unreadable instant: the caller passes a clock.
 */
export function staffEntryConsentDate(at: Date | string): string {
  const d = typeof at === 'string' ? new Date(at) : at;
  if (Number.isNaN(d.getTime())) throw new RangeError('staffEntryConsentDate: not a date');
  return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', year: 'numeric', month: 'long', day: 'numeric' }).format(d);
}

/** The ONE composer: the browser's preview and the façade's stored text both come from it. */
export function staffEntryConsentSentence(input: { firmName?: string | null; date: string }): string {
  const firm = typeof input.firmName === 'string' ? input.firmName.trim() : '';
  const at = firm === '' ? INTAKE_SHARING_CONSENT_FALLBACK_FIRM : firm;
  return STAFF_ENTRY_CONSENT_TEMPLATE
    .replace(STAFF_ENTRY_CONSENT_DATE_TOKEN, () => input.date)
    .replace(STAFF_ENTRY_CONSENT_FIRM_TOKEN, () => at);
}

/** What the SPA sends to record the consent: a confirmation and nothing else (no text, no name, no date). */
export const STAFF_ENTRY_CONSENT_REQUEST_KEYS = ['confirmed'] as const;

// ── The client's side (docs/40 §4.7, Matt 2026-10-27; provisional mark wording) ─────────────────

/** The per-answer mark on the CLIENT's forms. The client never sees which advisor. */
export function clientStaffMark(date: string): string {
  return `Entered by your advisor, ${date}`;
}

/** The notice at the top of the client's form while any staff-entered answer remains (Matt, 2026-10-27). */
export function clientStaffNotice(firmName: string | null | undefined, date: string): string {
  const firm = typeof firmName === 'string' && firmName.trim() !== '' ? firmName.trim() : INTAKE_SHARING_CONSENT_FALLBACK_FIRM;
  return `Your advisor at ${firm} started this with you on ${date}. You can review and change any answer. Answers your advisor entered are marked.`;
}

/**
 * The note on the client's Social Security number when their advisor replaced a number the CLIENT
 * had entered (docs/43 §8.4, D17, slice 9; appcore's `replacedClientValueAt`). Matt's wording,
 * verbatim. The client is never told which advisor.
 */
export function clientSsnReplacedNote(firmName: string | null | undefined, date: string): string {
  const firm = typeof firmName === 'string' && firmName.trim() !== '' ? firmName.trim() : INTAKE_SHARING_CONSENT_FALLBACK_FIRM;
  return `Your advisor at ${firm} updated your Social Security number on ${date}.`;
}

/** The per-answer mark on the FIRM's tabs (docs/43 D9). `name` null reads "a team member". */
export function firmStaffMark(name: string | null | undefined, date: string): string {
  const who = typeof name === 'string' && name.trim() !== '' ? name.trim() : 'a team member';
  return `Entered by ${who}, ${date}`;
}
